import type React from 'react';
import { useEffect, useState } from 'react';
import type { Advertisement } from '../types';
import { authenticatedFetch } from '../utils/api';

interface AdvertisementListProps {
  onAddAd: () => void;
  onEditAd: (ad: Advertisement) => void;
}

const AdvertisementList: React.FC<AdvertisementListProps> = ({ onAddAd, onEditAd }) => {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  // Helper to convert relative image URLs to absolute
  const getAbsoluteImageUrl = (url: string | undefined): string => {
    if (!url) return '';
    // If already absolute (starts with http:// or https://), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If relative, prepend API base URL
    return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/advertisements`);
      if (!response.ok) throw new Error('Failed to fetch advertisements');
      const data = await response.json();
      setAdvertisements(data.advertisements || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/advertisements/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete advertisement');

      await fetchAdvertisements();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleToggleActive = async (ad: Advertisement) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/advertisements/${ad.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...ad, active: !ad.active }),
      });

      if (!response.ok) throw new Error('Failed to update advertisement');

      await fetchAdvertisements();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const getTargetingInfo = (ad: Advertisement): string => {
    if (ad.showAlways) return 'All searches';

    const parts: string[] = [];
    if (ad.targetKeywords && ad.targetKeywords.length > 0) {
      parts.push(`Keywords: ${ad.targetKeywords.slice(0, 3).join(', ')}${ad.targetKeywords.length > 3 ? '...' : ''}`);
    }
    if (ad.targetIndexes && ad.targetIndexes.length > 0) {
      parts.push(`Indexes: ${ad.targetIndexes.length}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'Not targeted';
  };

  const getPlacementBadge = (ad: Advertisement): string => {
    if (ad.type === 'modal') return 'Modal';
    if (ad.placement) {
      return ad.placement
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'Banner';
  };

  const handleExportCSV = () => {
    // Convert advertisements to CSV format
    const headers = [
      'name',
      'type',
      'placement',
      'title',
      'imageUrl',
      'imageWidth',
      'imageHeight',
      'altText',
      'clickUrl',
      'utmSource',
      'utmMedium',
      'utmCampaign',
      'utmContent',
      'utmTerm',
      'showAlways',
      'targetKeywords',
      'targetIndexes',
      'startDate',
      'endDate',
      'priority',
      'active',
    ];

    const rows = advertisements.map((ad) => [
      ad.name,
      ad.type,
      ad.placement || '',
      ad.title || '',
      ad.imageUrl,
      ad.imageWidth || '',
      ad.imageHeight || '',
      ad.altText || '',
      ad.clickUrl,
      ad.utmSource || '',
      ad.utmMedium || '',
      ad.utmCampaign || '',
      ad.utmContent || '',
      ad.utmTerm || '',
      ad.showAlways,
      ad.targetKeywords?.join(';') || '',
      ad.targetIndexes?.join(';') || '',
      ad.startDate,
      ad.endDate,
      ad.priority,
      ad.active,
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

    // Download the CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advertisements-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());
        const headers = lines[0].split(',').map((h) => h.replace(/"/g, '').trim());

        const importedAds: Partial<Advertisement>[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map((v) => v.replace(/"/g, '').trim());
          const ad: any = {};

          headers.forEach((header, index) => {
            const value = values[index];
            if (header === 'showAlways' || header === 'active') {
              ad[header] = value === 'true';
            } else if (header === 'priority' || header === 'imageWidth' || header === 'imageHeight') {
              ad[header] = value ? parseInt(value, 10) : undefined;
            } else if (header === 'targetKeywords' || header === 'targetIndexes') {
              ad[header] = value ? value.split(';').filter((v: string) => v) : [];
            } else {
              ad[header] = value || undefined;
            }
          });

          importedAds.push(ad);
        }

        // Upload each advertisement
        for (const ad of importedAds) {
          await authenticatedFetch(`${API_BASE_URL}/api/advertisements`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(ad),
          });
        }

        await fetchAdvertisements();
        alert(`Successfully imported ${importedAds.length} advertisements`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to import CSV');
      }
    };

    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const isDateActive = (ad: Advertisement): boolean => {
    const now = new Date();
    const start = new Date(ad.startDate);
    const end = new Date(ad.endDate);
    return now >= start && now <= end;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-zinc-600">Loading advertisements...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600 bg-red-50 border border-red-200 rounded-lg cursor-pointer">
        Error: {error}
        <button
          type="button"
          onClick={fetchAdvertisements}
          className="ml-3 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto cursor-pointer">
      <div className="flex justify-between items-start mb-8 pb-4 border-b border-zinc-200 cursor-pointer">
        <div>
          <h3 className="text-2xl font-semibold text-zinc-900 mb-1 cursor-pointer">Advertisement Management</h3>
          <p className="text-sm text-zinc-500 cursor-pointer">Manage banner ads and modal advertisements</p>
        </div>
        <div className="flex gap-3 items-center">
          <label className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium cursor-pointer hover:bg-zinc-50 hover:border-zinc-400 transition-all inline-block">
            Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          <button
            type="button"
            onClick={handleExportCSV}
            className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium hover:bg-zinc-50 hover:border-zinc-400 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={advertisements.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={onAddAd}
            className="rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition-all cursor-pointer"
          >
            Add Advertisement
          </button>
        </div>
      </div>

      {advertisements.length === 0 ? (
        <div className="text-center py-12 px-6 bg-zinc-50 rounded-lg border border-dashed border-zinc-300 cursor-pointer">
          <p className="mb-4 text-zinc-600 text-base cursor-pointer">No advertisements yet</p>
          <button
            type="button"
            onClick={onAddAd}
            className="px-5 py-2 bg-zinc-900 text-white rounded-lg text-sm font-medium hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            Create your first advertisement
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 cursor-pointer">
          {advertisements.map((ad) => (
            <div key={ad.id} className={`bg-white border border-zinc-200 rounded-lg overflow-hidden`}>
              <div className="relative w-full h-48 bg-zinc-100 flex items-center justify-center overflow-hidden cursor-pointer">
                {ad.images && ad.images.length > 0 && ad.images[0]?.url ? (
                  <>
                    <img
                      src={getAbsoluteImageUrl(ad.images[0].url)}
                      alt={ad.images[0].altText || ad.name}
                      className="w-full h-full object-contain cursor-pointer"
                    />
                    {ad.images.length > 1 && (
                      <div className="absolute top-2 right-2 bg-indigo-500 text-white px-2 py-1 rounded text-xs font-semibold cursor-pointer">
                        {ad.images.length} images
                      </div>
                    )}
                  </>
                ) : ad.imageUrl ? (
                  <img
                    src={getAbsoluteImageUrl(ad.imageUrl)}
                    alt={ad.altText || ad.name}
                    className="w-full h-full object-contain cursor-pointer"
                  />
                ) : (
                  <div className="text-zinc-400 text-sm cursor-pointer">No Image</div>
                )}
              </div>

              <div className="p-4 cursor-pointer">
                <div className="flex justify-between items-start mb-3 cursor-pointer">
                  <h4 className="text-base font-semibold text-zinc-900 flex-1 cursor-pointer">{ad.name}</h4>
                  <div className="flex gap-1.5 flex-wrap cursor-pointer">
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-900 text-[11px] font-medium uppercase tracking-wide cursor-pointer">
                      {getPlacementBadge(ad)}
                    </span>
                    {!isDateActive(ad) && (
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[11px] font-medium uppercase tracking-wide cursor-pointer">
                        Scheduled
                      </span>
                    )}
                    {!ad.active && (
                      <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[11px] font-medium uppercase tracking-wide cursor-pointer">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-4 cursor-pointer">
                  <div className="flex py-1.5 text-sm border-b border-zinc-100 last:border-b-0 cursor-pointer">
                    <span className="font-medium text-zinc-500 w-24 flex-shrink-0 cursor-pointer">Targeting:</span>
                    <span className="text-zinc-700 flex-1 cursor-pointer">{getTargetingInfo(ad)}</span>
                  </div>
                  <div className="flex py-1.5 text-sm border-b border-zinc-100 last:border-b-0 cursor-pointer">
                    <span className="font-medium text-zinc-500 w-24 flex-shrink-0 cursor-pointer">Schedule:</span>
                    <span className="text-zinc-700 flex-1 cursor-pointer">
                      {new Date(ad.startDate).toLocaleDateString()} - {new Date(ad.endDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex py-1.5 text-sm border-b border-zinc-100 last:border-b-0 cursor-pointer">
                    <span className="font-medium text-zinc-500 w-24 flex-shrink-0 cursor-pointer">Priority:</span>
                    <span className="text-zinc-700 flex-1 cursor-pointer">{ad.priority}</span>
                  </div>
                  {((ad.images && ad.images.length > 0 && ad.images[0]?.clickUrl) || ad.clickUrl) && (
                    <div className="flex py-1.5 text-sm border-b border-zinc-100 last:border-b-0 cursor-pointer">
                      <span className="font-medium text-zinc-500 w-24 flex-shrink-0 cursor-pointer">URL:</span>
                      <span className="text-zinc-700 flex-1 truncate cursor-pointer">
                        {(ad.images && ad.images.length > 0 && ad.images[0]?.clickUrl) || ad.clickUrl}
                      </span>
                    </div>
                  )}
                  {(ad.utmSource || ad.utmMedium || ad.utmCampaign) && (
                    <div className="flex py-1.5 text-sm border-b border-zinc-100 last:border-b-0 cursor-pointer">
                      <span className="font-medium text-zinc-500 w-24 flex-shrink-0 cursor-pointer">UTM:</span>
                      <span className="text-zinc-700 flex-1 cursor-pointer">
                        {[ad.utmSource, ad.utmMedium, ad.utmCampaign].filter(Boolean).join(' / ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onEditAd(ad)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-white bg-zinc-900 hover:bg-zinc-800 transition-all cursor-pointer"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(ad)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${ad.active ? 'border border-zinc-300 text-zinc-700 hover:bg-zinc-50' : 'bg-zinc-700 text-white hover:bg-zinc-600'}`}
                  >
                    {ad.active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(ad.id)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-all cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvertisementList;
